import { InvoiceAction, InvoiceMutationDto, InvoiceStatus } from '../../../config/finance/invoiceConfig'
import { useFormikContext } from 'formik'
import { AxiosError, AxiosRequestConfig } from 'axios'
import { useDispatch } from 'react-redux'
import { toast, ToastContentProps } from 'react-toastify'
import { Text } from '@fluentui/react'
import ErrorPopover from '../../../components/Toast/ErrorPopover'
import { normalizeAxiosError } from '../../../reducers/errorReducer'
import * as React from 'react'
import useAxios from '../../useAxios'
import { useDocumentToastStyles } from '../../../components/Toast/DocumentToastContent'
import { defaultToastOptions, QueueStatus, ToastOptionsMap } from '../../useQueueStateHandler'
import { useTranslation } from 'react-i18next'

export type PersistInvoiceDto = {
    invoiceId: string
    invoiceNumber: string
    children?: PersistInvoiceDto[]
}

export type PersistInvoice = (
    invoiceStatus: InvoiceStatus,
    callback: (data: PersistInvoiceDto) => void,
) => Promise<void>

export default function usePersistInvoice(): PersistInvoice {
    const axios = useAxios()
    const dispatch = useDispatch()
    const { t: translate } = useTranslation()

    const { values, submitForm } = useFormikContext<InvoiceMutationDto>()
    const toastStyles = useDocumentToastStyles()

    return (invoiceStatus, callback): Promise<void> => {
        if (values.children?.length > 0) {
            for (let i = 0; i < values.children.length; i++) {
                const child = values.children[i]
                values.children[i] = {
                    ...values,
                    ...child,
                    //cleanup subinvoice
                    children: [],
                    lineItemGroups: [],
                }
            }
        }

        const submit = submitForm() as unknown as Promise<Pick<AxiosRequestConfig, 'url' | 'method'> | undefined>
        return submit.then(params => {
            if (params === undefined) {
                // The form didn't validate.
                return
            }
            dispatch({ type: InvoiceAction.ResetOverview })

            toast
                .promise(
                    axios
                        .request({
                            ...params,
                            data: { ...values, status: invoiceStatus },
                            headers: { SuppressErrors: true },
                        })
                        .then(result => result.data),
                    {
                        pending: {
                            ...ToastOptionsMap.get(QueueStatus.Queued),
                            render: () => {
                                return (
                                    <div className={toastStyles.toaster}>
                                        <Text>{translate('invoice.toast.queued', { number: values.number })}</Text>
                                    </div>
                                )
                            },
                        },
                        success: {
                            ...ToastOptionsMap.get(QueueStatus.Finished),
                            render: () => {
                                return (
                                    <div className={toastStyles.toaster}>
                                        <Text>{translate('invoice.toast.finished', { number: values.number })}</Text>
                                    </div>
                                )
                            },
                        },
                        error: {
                            ...ToastOptionsMap.get(QueueStatus.Failed),
                            render: (props: ToastContentProps<AxiosError>) => {
                                return (
                                    <div className={toastStyles.toaster}>
                                        <Text>{translate('invoice.toast.failed', { number: values.number })}</Text>
                                        <ErrorPopover error={normalizeAxiosError(props.data)} styles={toastStyles} />
                                    </div>
                                )
                            },
                        },
                    },
                    defaultToastOptions,
                )
                .then((result: PersistInvoiceDto) => callback(result))
        })
    }
}
